/**
 * Facture pro forma (modèle FedEx « FACTURE PRO FORMA / PRO FORMA INVOICE »),
 * en lecture (impression) ou en saisie (client). Même mise en page dans les
 * deux cas, au format A4 à l'impression.
 * champs : { date_export, references, expediteur, destinataire, importateur,
 *            pays_export, pays_fabrication, pays_destination, awb, signataire,
 *            devise, lignes: [{ nb_colis, emballage, description, poids,
 *            quantite, unite, valeur_unitaire }] }
 */
import { Plus, Trash2 } from "lucide-react";
import { ChampDate, SelectPays } from "./ChampsFacture";
import { paysEnAnglais } from "@/lib/pays";

export const LIGNE_VIDE = { nb_colis: "", emballage: "", description: "", poids: "", quantite: "", unite: "", valeur_unitaire: "" };
// Nombre saisi librement : « 12,50 € », « 1 250,00 », « 1.250,5 », « 7.90 EUR »…
export const num = (v) => {
  if (v === null || v === undefined) return null;
  let t = String(v).replace(/[^\d.,-]/g, "");
  if (!/\d/.test(t)) return null;
  const d = Math.max(t.lastIndexOf(","), t.lastIndexOf("."));
  if (d >= 0 && t.includes(",") && t.includes(".")) t = t.slice(0, d).replace(/[.,]/g, "") + "." + t.slice(d + 1);
  else t = t.replace(",", ".");
  const n = parseFloat(t);
  return Number.isFinite(n) ? n : null;
};
// Valeur totale d'une ligne : quantité × valeur unitaire (quantité vide = 1).
export const totalLigne = (l) => (num(l.valeur_unitaire) === null ? null : (num(l.quantite) ?? 1) * num(l.valeur_unitaire));
const fmt = (n, d = 2) => (n === null || n === undefined ? "" : n.toLocaleString("fr-FR", { minimumFractionDigits: d, maximumFractionDigits: d }));
// Dates de la facture au format américain MM/DD/YYYY.
const dateFr = (iso) => { const m = (iso || "").match(/^(\d{4})-(\d{2})-(\d{2})$/); return m ? `${m[2]}/${m[3]}/${m[1]}` : ""; };

export function totaux(lignes = []) {
  return lignes.reduce((t, l) => ({
    nb_colis: t.nb_colis + (parseInt(num(l.nb_colis) || 0, 10) || 0),
    poids: t.poids + (num(l.poids) || 0),
    valeur: t.valeur + (totalLigne(l) || 0),
  }), { nb_colis: 0, poids: 0, valeur: 0 });
}

function Libelle({ fr, en, oblig }) {
  return (
    <p className="text-[10px] leading-tight text-neutral-600">
      {fr}{oblig && <span className="text-red-600 print:hidden"> *</span>}<br /><span className="italic">{en}</span>
    </p>
  );
}

export function FactureDocument({ champs, date, editable = false, onChange, devises = ["EUR"], erreurs = [] }) {
  const c = champs || {};
  const lignes = c.lignes?.length ? c.lignes : (editable ? [LIGNE_VIDE] : []);
  const t = totaux(lignes);
  const set = (k, v) => onChange?.({ ...c, [k]: v });
  const setLigne = (i, k, v) => set("lignes", lignes.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  const ajouter = () => set("lignes", [...lignes, { ...LIGNE_VIDE }]);
  const retirer = (i) => set("lignes", lignes.filter((_, j) => j !== i));
  const err = (k) => erreurs.includes(k);

  const champ = (k, { multi = false, type = "text", placeholder = "" } = {}) => {
    if (!editable) return <p className={`text-sm whitespace-pre-line min-h-[1.25rem] ${multi ? "" : "font-medium"}`}>{type === "date" ? dateFr(c[k]) : type === "pays" ? paysEnAnglais(c[k]) : c[k]}</p>;
    const cls = `w-full bg-sky-50/60 border ${err(k) ? "border-red-500" : "border-transparent"} focus:border-sky-500 rounded px-1.5 py-1 text-sm text-neutral-900 outline-none`;
    if (type === "date") return <ChampDate value={c[k] || ""} onChange={(v) => set(k, v)} className={cls} testId={`f-${k}`} />;
    if (type === "pays") return <SelectPays value={c[k] || ""} onChange={(v) => set(k, v)} className={cls} testId={`f-${k}`} />;
    return multi
      ? <textarea rows={4} value={c[k] || ""} onChange={(e) => set(k, e.target.value)} className={`${cls} resize-y`} placeholder={placeholder} data-testid={`f-${k}`} />
      : <input type={type} value={c[k] || ""} onChange={(e) => set(k, e.target.value)} className={cls} placeholder={placeholder} data-testid={`f-${k}`} />;
  };
  const cell = "border-neutral-800 p-2";
  const inputLigne = (i, k, cls = "") => (editable
    ? <input value={lignes[i][k] || ""} onChange={(e) => setLigne(i, k, e.target.value)} inputMode={["nb_colis", "poids", "quantite", "valeur_unitaire"].includes(k) ? "decimal" : undefined}
      className={`w-full bg-sky-50/60 rounded px-1 py-1 text-xs text-neutral-900 outline-none focus:ring-1 focus:ring-sky-500 ${cls}`} data-testid={`l${i}-${k}`} />
    : <span className={`text-xs ${cls}`}>{lignes[i][k]}</span>);

  return (
    <div className="facture-print bg-white text-neutral-900 mx-auto w-full max-w-[210mm] shadow-sm print:shadow-none border border-neutral-300 print:border-0 p-4 sm:p-6 text-[13px]" data-testid="facture-document">
      <h1 className="text-center font-extrabold text-xl sm:text-2xl tracking-tight border-y-2 border-neutral-800 py-1 mb-0">FACTURE PRO FORMA / PRO FORMA INVOICE</h1>
      <div className="grid grid-cols-1 sm:grid-cols-2 print:grid-cols-2 border-x border-b border-neutral-800">
        <div className="sm:border-r print:border-r border-neutral-800">
          <div className={`${cell} border-b`}><Libelle fr="Date d'exportation" en="Date of Exportation" oblig={editable} />{champ("date_export", { type: "date" })}</div>
          <div className={`${cell} border-b`}><Libelle fr="Expéditeur/Exportateur (nom et adresse au complet)" en="Shipper/Exporter (complete name and address)" oblig={editable} />{champ("expediteur", { multi: true })}</div>
          <div className={`${cell} border-b`}><Libelle fr="Pays d'exportation" en="Country of Export" oblig={editable} />{champ("pays_export", { type: "pays" })}</div>
          <div className={`${cell} border-b`}><Libelle fr="Pays de fabrication" en="Country of Manufacture" oblig={editable} />{champ("pays_fabrication", { type: "pays" })}</div>
          <div className={cell}><Libelle fr="Pays de destination finale" en="Country of Ultimate Destination" oblig={editable} />{champ("pays_destination", { type: "pays" })}</div>
        </div>
        <div className="border-t sm:border-t-0 print:border-t-0 border-neutral-800">
          <div className={`${cell} border-b`}><Libelle fr="Références d'exportation (c-à-d no. de commande, no. de facture, etc.)" en="Export references (i.e. order no., invoice no., etc.)" />{champ("references")}</div>
          <div className={`${cell} border-b`}><Libelle fr="Destinataire (nom et adresse au complet)" en="Consignee (complete name and address)" oblig={editable} />{champ("destinataire", { multi: true })}</div>
          <div className={cell}><Libelle fr="Importateur - s'il s'agit d'un autre que le destinataire (nom et adresse au complet)" en="Importer - if other than consignee (complete name and address)" />{champ("importateur", { multi: true })}</div>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3 border-x border-b border-neutral-800 p-2">
        <div className="flex-1 min-w-[220px]"><Libelle fr="Lettre de transport aérien internationale/N° de suivi" en="International Air Waybill/Tracking No." /></div>
        <div className="w-56 border border-neutral-800 px-1">{champ("awb")}</div>
        {(editable || c.devise) && (
          <div className="flex items-center gap-1 text-xs">
            <span className="text-neutral-600">Devise / Currency</span>
            {editable
              ? <select value={c.devise || "EUR"} onChange={(e) => set("devise", e.target.value)} className="border border-neutral-400 rounded px-1 py-0.5 bg-white" data-testid="f-devise">
                {devises.map((d) => <option key={d}>{d}</option>)}
              </select>
              : <b>{c.devise}</b>}
          </div>
        )}
      </div>

      <div className="overflow-x-auto border-x border-neutral-800">
        <table className="w-full border-collapse text-center">
          <thead>
            <tr className="text-[10px] leading-tight text-neutral-600 align-top">
              {[["Nbre de colis", "No. of Pkgs", "w-14"], ["Type d'emballage", "Type of Packaging", "w-24"], ["Description complète des marchandises, marques et numéros", "Full Description of Goods, Marks & Numbers", ""],
                ["Poids (kg)", "Weight (kg)", "w-16"], ["Quantité / unité de mesure", "Quantity / Unit of Measure", "w-24"], ["Valeur unitaire", "Unit Value", "w-20"], ["Valeur totale", "Total Value", "w-24"]]
                .map(([fr, en, w]) => <th key={fr} className={`border border-neutral-800 p-1 font-normal ${w}`}>{fr}<br /><i>{en}</i></th>)}
              {editable && <th className="border border-neutral-800 w-8 print:hidden" />}
            </tr>
          </thead>
          <tbody>
            {lignes.map((l, i) => {
              const vt = totalLigne(l);
              return (
                <tr key={i} className="align-top" data-testid={`ligne-${i}`}>
                  <td className="border-x border-neutral-800 p-1">{inputLigne(i, "nb_colis", "text-center")}</td>
                  <td className="border-x border-neutral-800 p-1">{inputLigne(i, "emballage")}</td>
                  <td className="border-x border-neutral-800 p-1 text-left">
                    {editable
                      ? <textarea rows={2} value={l.description || ""} onChange={(e) => setLigne(i, "description", e.target.value)}
                        className="w-full bg-sky-50/60 rounded px-1 py-1 text-xs text-neutral-900 outline-none focus:ring-1 focus:ring-sky-500 resize-y" data-testid={`l${i}-description`} />
                      : <span className="text-xs whitespace-pre-line">{l.description}</span>}
                  </td>
                  <td className="border-x border-neutral-800 p-1">{inputLigne(i, "poids", "text-right")}</td>
                  <td className="border-x border-neutral-800 p-1">
                    <div className="flex gap-1">{inputLigne(i, "quantite", "text-right")}{editable ? <input value={l.unite || ""} onChange={(e) => setLigne(i, "unite", e.target.value)} placeholder="pcs"
                      className="w-12 bg-sky-50/60 rounded px-1 py-1 text-xs text-neutral-900 outline-none" data-testid={`l${i}-unite`} /> : <span className="text-xs">{l.unite}</span>}</div>
                  </td>
                  <td className="border-x border-neutral-800 p-1">{inputLigne(i, "valeur_unitaire", "text-right")}</td>
                  <td className="border-x border-neutral-800 p-1 text-right text-xs font-semibold">{fmt(vt)}</td>
                  {editable && (
                    <td className="border-x border-neutral-800 p-1 print:hidden">
                      <button type="button" onClick={() => retirer(i)} className="p-1 text-neutral-500 hover:text-red-600" aria-label="Retirer la ligne"><Trash2 size={14} /></button>
                    </td>
                  )}
                </tr>
              );
            })}
            {/* hauteur minimale du tableau, comme sur le formulaire papier */}
            {Array.from({ length: Math.max(0, 6 - lignes.length) }, (_, i) => (
              <tr key={`v${i}`} className="h-7">{Array.from({ length: editable ? 8 : 7 }, (_, j) => <td key={j} className="border-x border-neutral-800" />)}</tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="text-[10px] text-neutral-600">
              <td colSpan={2} className="border border-neutral-800 p-1">Nbre total de colis<br /><i>Total No. of Pkgs.</i><p className="text-sm font-bold text-neutral-900" data-testid="total-colis">{t.nb_colis || ""}</p></td>
              <td colSpan={2} className="border border-neutral-800 p-1">Poids total<br /><i>Total Weight</i><p className="text-sm font-bold text-neutral-900" data-testid="total-poids">{t.poids ? `${fmt(t.poids, 3).replace(/,?0+$/, "")} kg` : ""}</p></td>
              <td colSpan={editable ? 4 : 3} className="border border-neutral-800 p-1">Valeur totale de la facture<br /><i>Total Invoice Value</i><p className="text-sm font-bold text-neutral-900" data-testid="total-valeur">{t.valeur ? `${fmt(t.valeur)} ${c.devise || "EUR"}` : ""}</p></td>
            </tr>
          </tfoot>
        </table>
      </div>
      {editable && (
        <button type="button" onClick={ajouter} className="print:hidden mt-2 inline-flex items-center gap-1.5 text-sm text-sky-700 hover:underline" data-testid="ajouter-ligne">
          <Plus size={15} /> Ajouter une ligne d'article
        </button>
      )}

      <ul className="mt-4 space-y-1.5 text-xs">
        <li>• Ces marchandises sont autorisées pour la destination ultime indiquée.<br /><i>These commodities are licensed for the ultimate destination shown.</i></li>
        <li>• Tout déroutement contraire à la loi américaine est interdit.<br /><i>Diversion contrary to United States law is prohibited.</i></li>
        <li>• Je déclare que tous les renseignements figurant sur cette facture sont exacts et véridiques.<br /><i>I declare all the information contained in this invoice to be true and correct.</i></li>
      </ul>
      <div className="mt-5 grid grid-cols-[1fr_170px] gap-6 items-end">
        <div>
          <p className="text-[10px] text-neutral-600">Signature de l'expéditeur ou de l'exportateur (dactylographier le nom et le titre, puis signer)<br /><i>Signature of Shipper/Exporter (type name and title and sign)</i></p>
          <div className="mt-1">{champ("signataire", { placeholder: "Nom et titre du signataire" })}</div>
          <div className="h-14 border-b border-neutral-800" />
          <p className="text-[10px] text-neutral-500 mt-0.5">Signature manuscrite après impression / Handwritten signature after printing</p>
        </div>
        <div>
          <p className="text-[10px] text-neutral-600">DATE</p>
          {editable
            ? <div className="border-b border-neutral-800 pb-1" data-testid="facture-date">{champ("date_document", { type: "date" })}</div>
            : <p className="text-sm font-semibold border-b border-neutral-800 pb-1" data-testid="facture-date">{dateFr(date)}</p>}
        </div>
      </div>
    </div>
  );
}
